import React, { useEffect, useState } from 'react';
import { privacyIncidentsAPI, usersAPI } from '../utils/api';

const DEFAULT_FORM = {
  severity: 'medium',
  incidentType: 'disclosure',
  occurredAt: '',
  discoveredAt: '',
  description: '',
  informationInvolved: '',
  individualsAffectedCount: 0,
  systemsAffected: '',
  mitigationMeasures: '',
  riskAssessment: '',
  reportedToCai: false,
  reportedToCaiAt: '',
  notifiedIndividuals: false,
  notifiedIndividualsAt: '',
  notes: '',
  affectedUserIds: [],
};

const statusLabel = {
  open: 'Ouvert',
  under_review: 'Analyse',
  reported: 'Déclaré',
  closed: 'Fermé',
};

const severityLabel = {
  low: 'Faible',
  medium: 'Moyen',
  high: 'Élevé',
  critical: 'Critique',
};

const typeLabel = {
  access: 'Accès non autorisé',
  use: 'Utilisation non autorisée',
  disclosure: 'Communication non autorisée',
  loss: 'Perte',
  breach: 'Atteinte / brèche',
  other: 'Autre',
};

const toIsoOrNull = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const PrivacyIncidentsPage = () => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [userOptions, setUserOptions] = useState([]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await privacyIncidentsAPI.getAll();
      setRows(res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du chargement du registre.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const res = await usersAPI.getAll({ limit: 500 });
        setUserOptions(res.data?.users || []);
      } catch (_err) {
        setUserOptions([]);
      }
    };
    loadUsers();
  }, []);

  const onChange = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await privacyIncidentsAPI.create({
        ...form,
        occurredAt: toIsoOrNull(form.occurredAt),
        discoveredAt: toIsoOrNull(form.discoveredAt),
        reportedToCaiAt: form.reportedToCai ? toIsoOrNull(form.reportedToCaiAt) : null,
        notifiedIndividualsAt: form.notifiedIndividuals ? toIsoOrNull(form.notifiedIndividualsAt) : null,
        individualsAffectedCount: Number(form.individualsAffectedCount) || 0,
        affectedUserIds: form.affectedUserIds,
      });
      setForm(DEFAULT_FORM);
      setMessage('Incident de confidentialité ajouté au registre.');
      load();
    } catch (err) {
      const firstValidationError = err.response?.data?.errors?.[0]?.msg;
      const firstValidationField = err.response?.data?.errors?.[0]?.path;
      setError(
        firstValidationError
          ? `${firstValidationField ? `${firstValidationField}: ` : ''}${firstValidationError}`
          : (err.response?.data?.message || 'Erreur lors de l’enregistrement.')
      );
    } finally {
      setSaving(false);
    }
  };

  const selectedAffectedUsers = userOptions.filter((user) => form.affectedUserIds.includes(user.id));

  const updateStatus = async (id, status) => {
    try {
      await privacyIncidentsAPI.updateStatus(id, { status });
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la mise à jour du statut.');
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Registre des incidents de confidentialité</h2>
          <p className="text-muted">Registre interne distinct des incidents cliniques, réservé à l’administration.</p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-body">
          {message && <div className="alert alert-success" style={{ marginBottom: 12 }}>✅ {message}</div>}
          {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>⚠ {error}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Gravité</label>
                <select className="form-select" value={form.severity} onChange={(e) => onChange('severity', e.target.value)}>
                  {Object.entries(severityLabel).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Type</label>
                <select className="form-select" value={form.incidentType} onChange={(e) => onChange('incidentType', e.target.value)}>
                  {Object.entries(typeLabel).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Survenu le</label>
                <input type="datetime-local" className="form-input" value={form.occurredAt} onChange={(e) => onChange('occurredAt', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Découvert le</label>
                <input type="datetime-local" className="form-input" value={form.discoveredAt} onChange={(e) => onChange('discoveredAt', e.target.value)} />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea className="form-textarea" value={form.description} onChange={(e) => onChange('description', e.target.value)} required />
            </div>

            <div className="form-group">
              <label className="form-label">Renseignements ou categories touches</label>
              <textarea className="form-textarea" value={form.informationInvolved} onChange={(e) => onChange('informationInvolved', e.target.value)} />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Personnes concernées</label>
                <select
                  className="form-select"
                  multiple
                  value={form.affectedUserIds}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions).map((opt) => opt.value);
                    onChange('affectedUserIds', selected);
                    onChange('individualsAffectedCount', selected.length);
                  }}
                  style={{ minHeight: 140 }}
                >
                  {userOptions.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.first_name} {user.last_name} - {user.email}
                    </option>
                  ))}
                </select>
                <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 6 }}>
                  Ctrl/Cmd + clic pour choisir plusieurs personnes.
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Nombre de personnes touchees</label>
                <input type="number" min="0" className="form-input" value={form.individualsAffectedCount} onChange={(e) => onChange('individualsAffectedCount', e.target.value)} />
              </div>
            </div>

            {selectedAffectedUsers.length > 0 && (
              <div className="form-group">
                <label className="form-label">Courriels des personnes concernées</label>
                <div className="card" style={{ background: 'var(--gray-50)' }}>
                  <div className="card-body" style={{ padding: 12 }}>
                    {selectedAffectedUsers.map((user) => (
                      <div key={user.id} style={{ fontSize: 14, marginBottom: 6 }}>
                        <strong>{user.first_name} {user.last_name}</strong> - {user.email}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Systemes touches</label>
              <input type="text" className="form-input" value={form.systemsAffected} onChange={(e) => onChange('systemsAffected', e.target.value)} />
            </div>

            <div className="form-group">
              <label className="form-label">Mesures d’atténuation</label>
              <textarea className="form-textarea" value={form.mitigationMeasures} onChange={(e) => onChange('mitigationMeasures', e.target.value)} />
            </div>

            <div className="form-group">
              <label className="form-label">Évaluation du risque de préjudice sérieux</label>
              <textarea className="form-textarea" value={form.riskAssessment} onChange={(e) => onChange('riskAssessment', e.target.value)} />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label"><input type="checkbox" checked={form.reportedToCai} onChange={(e) => onChange('reportedToCai', e.target.checked)} /> Déclaré à la CAI</label>
                <input type="datetime-local" className="form-input" value={form.reportedToCaiAt} onChange={(e) => onChange('reportedToCaiAt', e.target.value)} disabled={!form.reportedToCai} />
              </div>
              <div className="form-group">
                <label className="form-label"><input type="checkbox" checked={form.notifiedIndividuals} onChange={(e) => onChange('notifiedIndividuals', e.target.checked)} /> Personnes concernées avisées</label>
                <input type="datetime-local" className="form-input" value={form.notifiedIndividualsAt} onChange={(e) => onChange('notifiedIndividualsAt', e.target.value)} disabled={!form.notifiedIndividuals} />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea className="form-textarea" value={form.notes} onChange={(e) => onChange('notes', e.target.value)} />
            </div>

            <button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? 'Enregistrement...' : 'Ajouter au registre'}
            </button>
          </form>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {loading ? (
            <div style={{ padding: 32, textAlign: 'center' }}><div className="spinner" style={{ margin: 'auto' }} /></div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>No dossier</th>
                    <th>Découvert</th>
                    <th>Type</th>
                    <th>Gravité</th>
                    <th>Statut</th>
                    <th>Statut modifié le</th>
                    <th>Personnes concernées</th>
                    <th>CAI</th>
                    <th>Personnes avisées</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr><td colSpan={10} style={{ textAlign: 'center', color: 'var(--gray-500)', padding: 24 }}>Aucun incident enregistré.</td></tr>
                  )}
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td style={{ fontWeight: 600 }}>{row.incident_number}</td>
                      <td>{row.discovered_at ? new Date(row.discovered_at).toLocaleString('fr-CA') : '—'}</td>
                      <td>{typeLabel[row.incident_type] || row.incident_type}</td>
                      <td>{severityLabel[row.severity] || row.severity}</td>
                      <td>{statusLabel[row.status] || row.status}</td>
                      <td>{row.status_updated_at ? new Date(row.status_updated_at).toLocaleString('fr-CA') : '—'}</td>
                      <td>
                        {Array.isArray(row.affected_users) && row.affected_users.length > 0
                          ? row.affected_users.map((user) => user.email).join(', ')
                          : '—'}
                      </td>
                      <td>{row.reported_to_cai ? 'Oui' : 'Non'}</td>
                      <td>{row.notified_individuals ? 'Oui' : 'Non'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {row.status !== 'under_review' && <button className="btn btn-outline btn-sm" onClick={() => updateStatus(row.id, 'under_review')}>Analyse</button>}
                          {row.status !== 'reported' && <button className="btn btn-outline btn-sm" onClick={() => updateStatus(row.id, 'reported')}>Déclaré</button>}
                          {row.status !== 'closed' && <button className="btn btn-outline btn-sm" onClick={() => updateStatus(row.id, 'closed')}>Fermer</button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrivacyIncidentsPage;
